import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, type ReactNode } from 'react';
import { api } from '../services/api';
import { storeTokens, clearStoredTokens } from '../services/api';
import { API_CONFIG } from '../config/api';
import { fetchMySubscription, type SubscriptionSummary } from '../services/subscriptions';

type UserRole = 'user' | 'admin' | 'super_admin';

interface User {
  id: number;
  name: string;
  email: string;
  role: UserRole;
  profile_image?: string | null;
  profile_image_url?: string | null;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  requestLoginOtp: (email: string) => Promise<void>;
  verifyLoginOtp: (email: string, otp: string) => Promise<void>;
  register: (name: string, email: string, password: string, confirmPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  setProfileImage: (imageUrl: string | null) => void;
  profileImage: string | null;
  subscription: SubscriptionSummary | null;
  refreshSubscription: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// Private/system paths that should always require authentication
const PRIVATE_PATH_PREFIXES = ['dashboard', 'admin', 'superadmin', 'login', 'signup', 'register', 'forgot-password', 'reset-password', 'backend', 'api', '00webapp'];

// System/static paths that should not be treated as profiles
const SYSTEM_PATHS = ['backend', 'api', '00webapp', 'assets', 'uploads', 'favicon', 'robots', 'sitemap'];

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionSummary | null>(null);

  const PUBLIC_PATHS = useMemo(
    () => new Set(['/', '/login', '/signup', '/register', '/forgot-password', '/reset-password']),
    []
  );

  // Helper function to check if a route is public (accessible without authentication)
  const isPublicRoute = useCallback((pathname: string): boolean => {
    // Check for explicit public prefixes (legacy routes)
    const hasPublicPrefix = /^\/(connect|user|u)\//.test(pathname);
    
    // Check if it's a known static public route
    const isPublicPath = PUBLIC_PATHS.has(pathname);
    
    // Check for root-level public profile
    // A root-level path (like /username) is public if:
    // 1. It's not the root itself (/)
    // 2. It doesn't start with known private/system prefixes
    // 3. It doesn't match known public routes (handled by PUBLIC_PATHS)
    const segments = pathname.split('/').filter(Boolean);
    const firstSegment = segments[0] || '';
    
    // Check if it's a system path (should not be treated as profile)
    const isSystemPath = SYSTEM_PATHS.some(prefix => firstSegment.startsWith(prefix) || firstSegment === prefix);
    
    // Check if it's a private path prefix
    const isPrivatePath = PRIVATE_PATH_PREFIXES.includes(firstSegment);
    
    const isRootLevelProfile = segments.length === 1 && !isPrivatePath && !isSystemPath;

    return hasPublicPrefix || isPublicPath || isRootLevelProfile;
  }, [PUBLIC_PATHS]);

  useEffect(() => {
    const checkRoute = () => {
      // Always run checkAuthStatus so we have user for conditional UI (e.g. public profile footer: "Go to Dashboard" vs "Login/Sign up")
      checkAuthStatus();
    };

    checkRoute();
  }, [isPublicRoute]);

  useEffect(() => {
    if (loading) return;

    const pathname = window.location.pathname;
    const isPublic = isPublicRoute(pathname);

    if (!user && !isPublic) {
      window.location.replace('/login');
    }
  }, [loading, user, isPublicRoute]);

  const convertToAbsoluteImageUrl = (imageUrl?: string | null): string | null => {
    if (!imageUrl) return null;

    let normalizedUrl = imageUrl.trim();
    if (!normalizedUrl) return null;

    if (normalizedUrl.startsWith('uploads/')) {
      normalizedUrl = `/${normalizedUrl}`;
    }

    // Already absolute - clean double prefixes if present
    if (normalizedUrl.startsWith('http')) {
      let cleaned = normalizedUrl;
      cleaned = cleaned.replace(/\/backend\/backend\//g, '/backend/');
      cleaned = cleaned.replace(/\/public\/public\//g, '/public/');
      return cleaned;
    }

    // Prevent double-prefix by cleaning base URL
    let baseUrl = API_CONFIG.BASE_URL;
    if (baseUrl.includes('/backend/backend')) {
      baseUrl = baseUrl.replace('/backend/backend', '/backend');
    }
    if (baseUrl.includes('/public/public')) {
      baseUrl = baseUrl.replace('/public/public', '/public');
    }

    if (normalizedUrl.startsWith('/uploads/profiles/')) {
      const filename = normalizedUrl.split('/').pop();
      if (!filename) return null;
      let url = `${baseUrl}/public/image.php?file=${filename}&type=profiles`;
      // Clean any double prefixes in final URL
      url = url.replace(/\/backend\/backend\//g, '/backend/');
      url = url.replace(/\/public\/public\//g, '/public/');
      return url;
    }
    if (normalizedUrl.startsWith('/uploads/qr/')) {
      const filename = normalizedUrl.split('/').pop();
      if (!filename) return null;
      let url = `${baseUrl}/public/image.php?file=${filename}&type=qr`;
      // Clean any double prefixes in final URL
      url = url.replace(/\/backend\/backend\//g, '/backend/');
      url = url.replace(/\/public\/public\//g, '/public/');
      return url;
    }
    if (normalizedUrl.startsWith('/api/uploads/collaborations/')) {
      const filename = normalizedUrl.split('/').pop();
      if (!filename) return null;
      let url = `${baseUrl}/public/image.php?file=${filename}&type=collaborations`;
      // Clean any double prefixes in final URL
      url = url.replace(/\/backend\/backend\//g, '/backend/');
      url = url.replace(/\/public\/public\//g, '/public/');
      return url;
    }
    if (normalizedUrl.startsWith('/')) {
      // Prevent double-prefix when prepending base URL
      if (normalizedUrl.startsWith('/backend/')) {
        normalizedUrl = normalizedUrl.replace('/backend/', '/');
      }
      if (normalizedUrl.startsWith('/public/')) {
        normalizedUrl = normalizedUrl.replace('/public/', '/');
      }
      let url = `${baseUrl}${normalizedUrl}`;
      // Clean any double prefixes in final URL
      url = url.replace(/\/backend\/backend\//g, '/backend/');
      url = url.replace(/\/public\/public\//g, '/public/');
      return url;
    }
    return normalizedUrl;
  };

  const updateProfileImage = (imageUrl: string | null) => {
    setProfileImage(convertToAbsoluteImageUrl(imageUrl));
  };

  const checkAuthStatus = async () => {
    try {
      const response = await api.get('/profiles/me');
      if (response.data.success) {
        setUser({
          id: response.data.profile.id,
          name: response.data.profile.name,
          email: response.data.profile.email,
          role: response.data.profile.role ?? 'user'
        });
        const profile = response.data.profile;
        updateProfileImage(
          profile.profile_image_url ??
          profile.profile_image ??
          profile.profile?.profile_image ??
          null
        );
        await loadSubscription();
      }
    } catch (error) {
      console.log('Not authenticated');
    } finally {
      setLoading(false);
    }
  };

  const loadSubscription = async () => {
    try {
      const response = await fetchMySubscription();
      if (response.data.success) {
        setSubscription(response.data.subscription);
      }
    } catch (error) {
      console.warn('Failed to load subscription details', error);
      setSubscription(null);
    }
  };

  const login = async (email: string, password: string, rememberMe = true) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      if (response.data.success) {
        // Persist tokens in localStorage/sessionStorage for PWA resilience
        if (response.data.access_token && response.data.refresh_token) {
          storeTokens(response.data.access_token, response.data.refresh_token, rememberMe);
        }
        setUser({
          ...response.data.user,
          role: response.data.user.role ?? 'user'
        });
        updateProfileImage(
          response.data.user?.profile_image_url ??
          response.data.user?.profile_image ??
          response.data.user?.profile?.profile_image ??
          null
        );
        await loadSubscription();
      } else {
        throw new Error(response.data.message);
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Login failed');
    }
  };

  const requestLoginOtp = async (email: string) => {
    try {
      const response = await api.post('/auth/request-otp', { email });
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to request login code');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to request login code');
    }
  };

  const verifyLoginOtp = async (email: string, otp: string) => {
    try {
      const response = await api.post('/auth/verify-otp', { email, otp });
      if (response.data.success) {
        // OTP logins always persist tokens (equivalent to "Remember me")
        if (response.data.access_token && response.data.refresh_token) {
          storeTokens(response.data.access_token, response.data.refresh_token, true);
        }
        setUser({
          ...response.data.user,
          role: response.data.user.role ?? 'user'
        });
        updateProfileImage(
          response.data.user?.profile_image_url ??
          response.data.user?.profile_image ??
          response.data.user?.profile?.profile_image ??
          null
        );
        await loadSubscription();
      } else {
        throw new Error(response.data.message || 'Invalid or expired code');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Invalid or expired code');
    }
  };

  const register = async (name: string, email: string, password: string, confirmPassword: string) => {
    try {
      const response = await api.post('/auth/register', {
        name,
        email,
        password,
        confirm_password: confirmPassword
      });
      if (response.data.success) {
        return response.data;
      } else {
        throw new Error(response.data.message);
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Registration failed');
    }
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearStoredTokens();
      setUser(null);
      setSubscription(null);
    }
  };

  const value = {
    user,
    loading,
    login,
    requestLoginOtp,
    verifyLoginOtp,
    register,
    logout,
    setProfileImage: updateProfileImage,
    profileImage,
    subscription,
    refreshSubscription: loadSubscription
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
